import React, { useContext, useMemo, useState } from "react";
import { Button, Dropdown, MenuProps } from "antd";
import { saveAs } from "file-saver";
import ExcelJS from "exceljs";
import { useTranslation } from "react-i18next";
import { SaleItemState } from "@/types/sales";
import dayjs from "dayjs";
import { DictContext } from "@/layout/context/DictContext";
import { docFetchs } from "@/apis/doc";
import { getFileUrl } from "@/apis/file";
import { RootState } from "@/store";
import { useSelector } from "react-redux";

interface ExportExcelButtonProps {
  // 兼容旧用法：外部仍可直接传入已准备好的 PI 明细列表。
  list?: Array<SaleItemState>;
  // 新用法：外部可延迟到用户真正点击导出模板时再返回最新选中数据。
  getList?: () => Array<SaleItemState>;
  // 禁用态由外部根据选中数量判断，避免组件内部为了判断 disabled 提前取数。
  disabled?: boolean;
}

const ExportExcelToPI: React.FC<ExportExcelButtonProps> = ({ list = [], getList, disabled }) => {
  const { t } = useTranslation();
  const { salePayMethod, currencys, printSetting } = useContext(DictContext);
  const curUser = useSelector((state: RootState) => state.userReducer.curUser);



  const [loading, setLoading] = useState(false);
  // 支持按需取数：勾选时不生成 PI 明细，用户选择导出模板时才读取最新选中数据。
  const getExportList = () => getList?.() ?? list;

  const { picUrl, name } = useMemo(() => {
    let [picUrl, name] = ['', ''];
    if (printSetting?.length > 0) {
      printSetting.forEach(item => {
        if (item.key === "companyName" && item.value) name = item.value
        if (item.key === "companyLogo" && item.value) picUrl = getFileUrl(item.value) || ''
      })
    }

    return { picUrl, name };

  }, [printSetting])

  const setBorder = (cell: ExcelJS.Cell | ExcelJS.Row, style: ExcelJS.BorderStyle = "medium") => {
    cell.border = {
      top: { style },
      bottom: { style },
      left: { style },
      right: { style },
    };
  };

  const setStyle = (
    cell: ExcelJS.Cell,
    font: Partial<ExcelJS.Font>,
    alignment: Partial<ExcelJS.Alignment>
  ) => {
    cell.font = font;
    cell.alignment = alignment;
  };

  // 模板一导出时才读取选中数据，避免勾选阶段做 Excel 数据准备。
  const exportToExcelOne = async () => {
    const list = getExportList();
    if (!list.length) return;

    setLoading(true);
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Sheet1", {
        properties: { defaultColWidth: 14 },
        views: [{ style: "pageBreakPreview", state: "split" }],
      });
      worksheet.pageSetup = { fitToPage: true, fitToWidth: 1 };

      //logo
      try {
        // 获取图片
        const response = await fetch(picUrl);
        const imageBuffer = await response.arrayBuffer();

        const logoId = workbook.addImage({
          buffer: imageBuffer,
          extension: "png",
        });
        worksheet.addImage(logoId, {
          tl: { col: 0, row: 0 },
          ext: { width: 290, height: 80 },
          editAs: "oneCell",
        });
      } catch (error) {
        console.log(error);
      }

      const headerRowIndex = 8;

      // 标题和表头
      worksheet.mergeCells("A1:H1");
      worksheet.mergeCells("A2:H2");
      worksheet.mergeCells("A3:H3");
      worksheet.mergeCells("A5:H5");
      worksheet.getCell("A1").value = name ?? "ZIONCOM (HONG KONG) TECHNOLOGY LIMITED";
      worksheet.getCell("A2").value =
        "Flat/rm 1403,14/f,bank of america tower,12 harcourt road,central,Hong Kong";
      worksheet.getCell("A3").value =
        "TEL : +852-2495-9788    FAX : +852-2435-9788    Website: www.totolink.net";
      worksheet.getCell("A5").value = t("sale.quotationSheet");

      setStyle(
        worksheet.getCell("A1"),
        { bold: true, size: 26, name: "Arial" },
        { vertical: "middle", horizontal: "center" }
      );
      setStyle(
        worksheet.getCell("A2"),
        { size: 12, name: "Arial" },
        { vertical: "middle", horizontal: "center" }
      );
      setStyle(
        worksheet.getCell("A3"),
        { size: 12, name: "Arial" },
        { vertical: "middle", horizontal: "center" }
      );
      setStyle(
        worksheet.getCell("A5"),
        { bold: true, size: 24, italic: true, underline: true },
        { vertical: "middle", horizontal: "center" }
      );

      // 基础信息
      worksheet.getCell("A6").value = "TO:";
      worksheet.getCell("B6").value =
        list[0]?.customer?.name1 ||
        list[0]?.customer?.name2 ||
        list[0]?.customer?.code;
      worksheet.getCell("E6").value = "MOV:";
      worksheet.getCell("F6").value =
        salePayMethod.find((item) => item.id === list[0]?.order?.payMethod)
          ?.desc || "";
      worksheet.getCell("A7").value = t("sale.effectiveDate");
      worksheet.getCell("B7").value = dayjs(list[0]?.order?.preFinishDate).format(
        "YYYY/MM/DD"
      );

      // 表头
      const headers = [
        t("sale.modelNo"),
        t("sale.mfgrNo"),
        t("sale.picture"),
        t("sale.descriptionOfProduct"),
        t("sale.chipSet"),
        t("sale.package"),
        t("sale.unitPrice"),
        "MOQ",
      ];
      worksheet.getRow(headerRowIndex).values = headers;
      worksheet.getRow(headerRowIndex).alignment = {
        vertical: "middle",
        horizontal: "center",
      };
      worksheet.getRow(headerRowIndex).font = { bold: true, name: 'Arial' };
      worksheet.getRow(headerRowIndex).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFB5C6EA" },
      };
      worksheet.getRow(headerRowIndex).height = 40;

      // 数据行
      let positionFileId: { [key: string]: string } = {};
      const data = list.map((item, index) => {
        const currency = currencys.find(
          (curr) => curr.key === item.order?.currency
        );
        positionFileId[`2-${headerRowIndex + index}`] = item.product?.picDocId || ""
        return [
          item.product?.model || "",
          item?.order?.mfgr || "",
          "",
          item.product?.desc || "",
          "",
          item.quantity || 0,
          `${item.price || 0} ${currency ? "(" + currency.desc + ")" : ""}`,
          "",
        ];
      });
      try {
        let positionFileIdCopy = { ...positionFileId }
        let ids = Object.values(positionFileIdCopy)
        let docs = await docFetchs(ids)

        for (let key in positionFileIdCopy) {
          let doc = docs.find(item => item.id === positionFileIdCopy[key])
          if (doc && doc.subs && doc.subs.length > 0) {
            positionFileId[key] = doc.subs[0].fileId
          } else positionFileId[key] = ""
        }

      } catch (error) {
        console.log(error);
      }

      worksheet.addRows(data);


      for (let k in positionFileId) {
        if (positionFileId[k] !== '') {
          let col = Number(k.split('-')[0])
          let row = Number(k.split('-')[1])
          let imgUrl = getFileUrl(positionFileId[k])
          // let imgUrl = getFileUrl('2bb14b15b85e40948da81c440cd02225')
          if (imgUrl) {
            let resp = await fetch(imgUrl)
            let imageBlob = await resp.blob();
            let imageBuffer = await imageBlob.arrayBuffer();

            let imgId = workbook.addImage({
              buffer: imageBuffer,
              extension: "png",
            });
            worksheet.addImage(imgId, {
              tl: { col, row },
              ext: { width: 140, height: 100 },
              editAs: "oneCell",
            });
          }
        }
      }

      // 列宽
      const colWidths = [20, 14, 20, 70, 40, 20, 20, 24];
      colWidths.forEach((w, i) => (worksheet.getColumn(i + 1).width = w));

      // 边框 & 行高
      for (let i = 0; i < list.length; i++) {
        const rowIdx = headerRowIndex + 1 + i;
        const row = worksheet.getRow(rowIdx);
        row.height = 80;
        row.alignment = {
          vertical: "middle",
          horizontal: "center",
          wrapText: true,
        };
        row.eachCell(cell => setBorder(cell));
      }

      // 备注部分
      const notesStart = headerRowIndex + list.length + 2;
      worksheet.addRows([
        [],
        [t("sale.note")],
        [t("sale.quotationNote1")],
        [t("sale.quotationNote2")],
        [t("sale.quotationNote3")],
        [t("sale.quotationNote4")],
      ]);

      for (let i = 0; i < 6; i++) {
        const row = worksheet.getRow(notesStart + i);
        row.font = { bold: true };
      }

      worksheet.getCell(`A${notesStart}`).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFF0F0F0" },
      };
      worksheet.getCell(`B${notesStart}`).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFF0F0F0" },
      };

      // 添加图片
      // try {
      //   const response = await fetch("/zion.png");
      //   const imageBlob = await response.blob();
      //   const imageBuffer = await imageBlob.arrayBuffer();

      //   const imageId = workbook.addImage({
      //     buffer: imageBuffer,
      //     extension: "png",
      //   });
      //   worksheet.addImage(imageId, "A1:B3");
      // } catch (error) {
      //   console.warn("图片加载失败:", error);
      // }

      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(new Blob([buffer]), "quotation.xlsx");
    } catch (e) {
      console.error(t("sale.exportFailed"), e);
    } finally {
      setLoading(false);
    }
  };

  // 模板二同样延迟取数，两个模板共用最新选中结果。
  const exportToExcelTwo = async () => {
    const list = getExportList();
    if (!list.length) return;

    setLoading(true);

    const setThinBorder = (sheet: ExcelJS.Worksheet, cellIndex: string[], position: 'left' | 'top' | 'right' | 'bottom') => {
      cellIndex.forEach(item => {
        sheet.getCell(item).border = { ...sheet.getCell(item).border, [position]: { style: "thin" } }
      })
    }
    const setFatBorder = (sheet: ExcelJS.Worksheet, cellIndex: string[], position: 'left' | 'top' | 'right' | 'bottom') => {
      cellIndex.forEach(item => {
        sheet.getCell(item).border = { ...sheet.getCell(item).border, [position]: { style: "medium" } }
      })
    }

    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Sheet1", {
        properties: { defaultColWidth: 14 },
        views: [{ style: "pageBreakPreview", state: "split" }],
      });
      worksheet.pageSetup = { fitToPage: true, fitToWidth: 1 };

      // 标题
      worksheet.mergeCells("A1:I1");
      worksheet.getCell("A1").value = t("sale.quotationTitle")

      setStyle(
        worksheet.getCell("A1"),
        { bold: true, size: 26, name: "Arial" },
        { vertical: "middle", horizontal: "center" }
      )

      // 基础信息
      worksheet.mergeCells("A2:D2");
      worksheet.getCell("A2").value = name ?? "ZIONCOM (VIETNAM) JOINT STOCK COMPANY.";
      setStyle(
        worksheet.getCell("A2"),
        { bold: true, size: 14, name: "Arial" },
        { vertical: "middle", horizontal: "left" }
      )

      worksheet.mergeCells("E2:I2");
      worksheet.getCell("E2").value = "CARVE VIET NAM TECHNOLOGY COMPANY LIMITED";
      setStyle(
        worksheet.getCell("E2"),
        { bold: true, size: 14, name: "Arial" },
        { vertical: "middle", horizontal: "left" }
      )

      worksheet.getRow(3).height = 60
      worksheet.mergeCells("A3:D3");
      worksheet.getCell("A3").value = "2A VSIP II-A, No.22 Road, Vietnam-Singapore Industrial Park II-A, Vinh Tan, Tan Uyen Province, Vietnam.";
      setStyle(
        worksheet.getCell("A3"),
        { bold: false, size: 14, name: "Arial" },
        { vertical: "middle", horizontal: "left", wrapText: true }
      )

      worksheet.mergeCells("E3:I3");
      worksheet.getCell("E3").value = "Số 5, Đường số 7, Khu công nghiệp Việt Nam – Singapore II, Phường Hoà Phú, Thành phố Thủ Dầu Một, Tỉnh Bình Dương, Việt Nam";
      setStyle(
        worksheet.getCell("E3"),
        { bold: false, size: 14, name: "Arial" },
        { vertical: "middle", horizontal: "left", wrapText: true }
      )

      worksheet.mergeCells("A4:B4");
      worksheet.mergeCells("C4:D4");
      worksheet.getCell('A4').value = t("sale.contactPerson")
      worksheet.getCell('C4').value = curUser.name1 || curUser.name2 || ''

      setStyle(
        worksheet.getCell('A4'),
        { bold: true, size: 14, name: "Arial" },
        { vertical: "middle", horizontal: "left" }
      )

      setStyle(
        worksheet.getCell('C4'),
        { bold: false, size: 14, name: "Arial" },
        { vertical: "middle", horizontal: "left" }
      )

      worksheet.mergeCells("A5:B5");
      worksheet.mergeCells("C5:D5");
      worksheet.getCell('A5').value = t("sale.officeTel")
      worksheet.getCell('C5').value = `(${curUser.areaCode})${curUser.mobile}`

      setStyle(
        worksheet.getCell('A5'),
        { bold: true, size: 14, name: "Arial" },
        { vertical: "middle", horizontal: "left" }
      )

      setStyle(
        worksheet.getCell('C5'),
        { bold: false, size: 14, name: "Arial" },
        { vertical: "middle", horizontal: "left" }
      )

      worksheet.mergeCells("E5:F5");
      worksheet.mergeCells("G5:I5");
      worksheet.getCell('E5').value = t("sale.contactPerson")
      worksheet.getCell('G5').value = list[0]?.customer?.name1 || list[0]?.customer?.name2 || ''

      setStyle(
        worksheet.getCell('E5'),
        { bold: true, size: 14, name: "Arial" },
        { vertical: "middle", horizontal: "left" }
      )

      setStyle(
        worksheet.getCell('G5'),
        { bold: false, size: 14, name: "Arial" },
        { vertical: "middle", horizontal: "left" }
      )

      worksheet.mergeCells("A6:B6");
      worksheet.mergeCells("C6:D6");
      worksheet.mergeCells("C7:D7");
      worksheet.getCell('A6').value = t("sale.emailLabel")
      worksheet.getCell('C6').value = ""
      // worksheet.getCell('C7').value = "邮箱2"

      setStyle(
        worksheet.getCell('A6'),
        { bold: true, size: 14, name: "Arial" },
        { vertical: "middle", horizontal: "left" }
      )

      setStyle(
        worksheet.getCell('C6'),
        { bold: false, size: 14, name: "Arial" },
        { vertical: "middle", horizontal: "left" }
      )
      setStyle(
        worksheet.getCell('C7'),
        { bold: false, size: 14, name: "Arial" },
        { vertical: "middle", horizontal: "left" }
      )

      worksheet.mergeCells("E6:F6");
      worksheet.mergeCells("G6:I6");
      worksheet.getCell('E6').value = t("sale.emailLabel")
      worksheet.getCell('G6').value = list[0]?.customer?.email || ''

      setStyle(
        worksheet.getCell('E6'),
        { bold: true, size: 14, name: "Arial" },
        { vertical: "middle", horizontal: "left" }
      )

      setStyle(
        worksheet.getCell('G6'),
        { bold: false, size: 14, name: "Arial" },
        { vertical: "middle", horizontal: "left" }
      )

      worksheet.mergeCells("B8:D8");
      worksheet.getCell('A8').value = t("sale.date")
      worksheet.getCell('B8').value = dayjs().format('MM,DD,YYYY')
      setStyle(
        worksheet.getCell('A8'),
        { bold: false, size: 14, name: "Arial" },
        { vertical: "middle", horizontal: "left" }
      )
      setStyle(
        worksheet.getCell('B8'),
        { bold: false, size: 14, name: "Arial" },
        { vertical: "middle", horizontal: "left" }
      )

      setThinBorder(worksheet, ['A2', 'A3', 'C4', 'C5', 'C6', 'C7', 'B8'], 'right')
      setFatBorder(worksheet, ['A2', 'E2'], 'top')
      setFatBorder(worksheet, ['A2', 'A3', 'A4', 'A5', 'A6', 'A7', 'A8'], 'left')
      setFatBorder(worksheet, ['E2', 'E3', 'I4', 'G5', 'G6', 'I7', 'I8'], 'right')
      setFatBorder(worksheet, ['A8', 'B8', 'E8', 'F8', 'G8', 'H8', 'I8'], 'bottom')

      //报价单号：
      worksheet.mergeCells("A9:B9");
      worksheet.mergeCells("C9:D9");
      worksheet.getCell('A9').value = t("sale.quotationNumber")
      worksheet.getCell('C9').value = list && list.length > 0 ? list[0].order.innerNumber : ''

      setStyle(
        worksheet.getCell('A9'),
        { bold: false, size: 14, name: "Arial" },
        { vertical: "middle", horizontal: "left" }
      )

      setStyle(
        worksheet.getCell('C9'),
        { bold: true, size: 14, name: "Arial" },
        { vertical: "middle", horizontal: "left" }
      )

      //敬语
      worksheet.mergeCells("A11:B11");
      worksheet.getCell('A11').value = t("sale.respectedCustomer")
      setStyle(
        worksheet.getCell('A11'),
        { bold: false, size: 14, name: "Microsoft JhengHei" },
        { vertical: "middle", horizontal: "left" }
      )

      worksheet.mergeCells("A12:I12");
      worksheet.getRow(12).height = 60
      worksheet.getCell("A12").value = t("sale.quotationIntro")
      setStyle(
        worksheet.getCell('A12'),
        { bold: false, size: 14, name: "Microsoft JhengHei" },
        { vertical: "top", horizontal: "left", wrapText: true }
      )


      // 表头
      worksheet.getRow(13).height = 24
      worksheet.getRow(14).height = 24
      worksheet.mergeCells("A13:A14")
      worksheet.mergeCells("B13:B14")
      worksheet.mergeCells("C13:C14")
      worksheet.mergeCells("D13:D14")
      worksheet.mergeCells("E13:E14")
      worksheet.mergeCells("F13:I13")
      worksheet.getCell("A13").value = t("sale.no")
      worksheet.getCell("B13").value = t("sale.quotationProductCode")
      worksheet.getCell("C13").value = t("sale.quotationProductDesc")
      worksheet.getCell("D13").value = t("sale.quotationUnit")
      worksheet.getCell("E13").value = t("sale.quotationCurrency")
      worksheet.getCell("F13").value = t("sale.moqPrice")
      worksheet.getCell("F14").value = "1K"
      worksheet.getCell("G14").value = "3K"
      worksheet.getCell("H14").value = "5K"
      worksheet.getCell("I14").value = "10K"

      worksheet.getRow(13).alignment = { vertical: "middle", horizontal: "center" };
      worksheet.getRow(13).font = { bold: true, name: 'Microsoft JhengHei', size: 14 };
      worksheet.getRow(13).eachCell(cell => cell.border = { top: { style: "medium" }, right: { style: "medium" }, bottom: { style: "medium" }, left: { style: "medium" }, })
      worksheet.getRow(14).alignment = { vertical: "middle", horizontal: "center" };
      worksheet.getRow(14).font = { bold: true, name: 'Microsoft JhengHei', size: 14 };
      worksheet.getRow(14).eachCell(cell => cell.border = { top: { style: "medium" }, right: { style: "medium" }, bottom: { style: "medium" }, left: { style: "medium" }, })

      // worksheet.getCell("F13").font = { bold: true, name: 'Microsoft JhengHei', size: 14 };

      //表格数据
      const colWidths = [10, 20, 60, 15, 15, 20, 20, 20, 20];
      colWidths.forEach((w, i) => (worksheet.getColumn(i + 1).width = w));

      const firstDataRowIdx = 15;
      list.forEach((item, index) => {
        const currency = currencys.find(
          (curr) => curr.key === item.order?.currency
        );
        let rowData = [
          index + 1,
          item.product?.code || '',
          item.product?.model || '',
          item.product?.uom || '',
          currency ? currency.desc : '',
          '',
          '',
          '',
          ''
        ]
        let newRow = worksheet.addRow(rowData)
        newRow.height = 30
        newRow.font = { bold: false, name: 'Microsoft JhengHei', size: 12 }
        newRow.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true }
        newRow.eachCell(cell => setBorder(cell, 'thin'))
        worksheet.getCell(firstDataRowIdx + index, 3).alignment = { horizontal: 'left', vertical: 'middle', wrapText: true }
      })
      let dataEndIndx = firstDataRowIdx + list.length - 1
      setFatBorder(worksheet, [
        `A${dataEndIndx}`,
        `B${dataEndIndx}`,
        `C${dataEndIndx}`,
        `D${dataEndIndx}`,
        `E${dataEndIndx}`,
        `F${dataEndIndx}`,
        `G${dataEndIndx}`,
        `H${dataEndIndx}`,
        `I${dataEndIndx}`,
      ], "bottom")

      // 备注部分
      const notesStart = firstDataRowIdx + list.length + 1;
      for (let i = 0; i < 7; i++) {
        worksheet.mergeCells(`A${notesStart + i}:I${notesStart + i}`)
      }
      worksheet.getCell(`A${notesStart}`).value = t("sale.attention")
      worksheet.getCell(`A${notesStart}`).font = { bold: true, size: 16, name: "Arial" }

      worksheet.getCell(`A${notesStart + 2}`).value = t("sale.quotationAttention1")
      worksheet.getCell(`A${notesStart + 2}`).font = { bold: false, size: 16, name: "Arial" }

      worksheet.getCell(`A${notesStart + 3}`).value = t("sale.quotationAttention2")
      worksheet.getCell(`A${notesStart + 3}`).font = { bold: false, size: 16, name: "Arial" }

      worksheet.getCell(`A${notesStart + 4}`).value = t("sale.quotationAttention3")
      worksheet.getCell(`A${notesStart + 4}`).font = { bold: false, size: 16, name: "Arial" }

      worksheet.getCell(`A${notesStart + 5}`).value = t("sale.quotationAttention4")
      worksheet.getCell(`A${notesStart + 5}`).font = { bold: false, size: 16, name: "Arial" }

      worksheet.mergeCells(`B${notesStart + 7}:D${notesStart + 7}`)
      worksheet.mergeCells(`E${notesStart + 7}:H${notesStart + 7}`)
      worksheet.getCell(`B${notesStart + 7}`).value = t("sale.customerConfirmation")
      setStyle(
        worksheet.getCell(`B${notesStart + 7}`),
        { bold: true, size: 14, name: "Arial" },
        { vertical: "middle", horizontal: "left" }
      )
      worksheet.getCell(`E${notesStart + 7}`).value = t("sale.zioncomVietnam")
      setStyle(
        worksheet.getCell(`E${notesStart + 7}`),
        { bold: true, size: 14, name: "Arial" },
        { vertical: "middle", horizontal: "right" }
      )

      worksheet.addRows([[""], [""], [""], [""]])
      //保存
      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(new Blob([buffer]), "quotationTwo.xlsx");
    } catch (e) {
      console.error(t("sale.exportFailed"), e);
    } finally {
      setLoading(false);
    }
  };

  const PITypes: MenuProps['items'] = [
    {
      key: "1",
      label: <div onClick={exportToExcelOne}>{t("sale.inquiryTemplate")}</div>
    },
    {
      key: "2",
      label: <div onClick={exportToExcelTwo}>{t("sale.quotationTemplate")}</div>
    },
    // {
    //   key: "3",
    //   label: <div>{t("requirement.inquiryTemplate")}</div>
    // },
  ]

  return (
    <Dropdown menu={{ items: PITypes }} placement="bottom" arrow disabled={disabled || loading}>
      <Button loading={loading} disabled={disabled}>
        {t("sale.exportPI")}
      </Button>
    </Dropdown>
  );
};

export default ExportExcelToPI;
